import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapComponent.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapComponent = ({
  center,
  zoom = 18,
  onMapClick,
  clusters = [], // Changed from markers to clusters
  onClusterClick,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const markersRef = useRef([]);

  // Initialize map
  useEffect(() => {
    if (map.current) return; // initialize map only once

    if (!center) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [center.lng, center.lat],
      zoom,
      pitch: 0,
      bearing: 0,
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add geolocate control
      map.current.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
          showUserHeading: true,
          fitBoundsOptions: {
            maxZoom: 20,
          },
        }),
        'top-right',
      );

      // Add click handler with visual feedback
      map.current.on('click', async (e) => {
        const { lng, lat } = e.lngLat;

        // Show loading popup immediately
        const loadingPopup = new mapboxgl.Popup()
          .setLngLat([lng, lat])
          .setHTML('<div style="padding: 10px; text-align: center;"><div>Loading location...</div></div>')
          .addTo(map.current);

        try {
          // Try to get address using reverse geocoding
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}&types=address`,
          );
          const data = await response.json();

          let address = '';
          if (data.features && data.features.length > 0) {
            address = data.features[0].place_name;
          }

          // Remove loading popup
          loadingPopup.remove();

          // Show success popup with add review option
          const successPopup = new mapboxgl.Popup()
            .setLngLat([lng, lat])
            .setHTML(`
              <div style="padding: 10px; max-width: 200px;">
                <div style="font-weight: bold; margin-bottom: 8px;">📍 ${address || 'Selected Location'}</div>
                <button id="add-review-btn" style="
                  background: #27ae60;
                  color: white;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                  width: 100%;
                ">Add Review Here</button>
              </div>
            `)
            .addTo(map.current);

          // Add click handler to the button
          setTimeout(() => {
            const btn = document.getElementById('add-review-btn');
            if (btn) {
              btn.onclick = () => {
                successPopup.remove();
                onMapClick({
                  lng,
                  lat,
                  address,
                });
              };
            }
          }, 100);
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          loadingPopup.remove();

          // Show error popup
          const errorPopup = new mapboxgl.Popup()
            .setLngLat([lng, lat])
            .setHTML(`
              <div style="padding: 10px; max-width: 200px;">
                <div style="color: #e74c3c; margin-bottom: 8px;">❌ Location Error</div>
                <button id="retry-review-btn" style="
                  background: #3498db;
                  color: white;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 14px;
                  width: 100%;
                ">Try Again</button>
              </div>
            `)
            .addTo(map.current);

          setTimeout(() => {
            const btn = document.getElementById('retry-review-btn');
            if (btn) {
              btn.onclick = () => {
                errorPopup.remove();
                onMapClick({
                  lng,
                  lat,
                  address: '',
                });
              };
            }
          }, 100);
        }
      });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [center, zoom, onMapClick]);

  // Update map center when center prop changes
  useEffect(() => {
    if (map.current && center && mapLoaded) {
      map.current.setCenter([center.lng, center.lat]);
    }
  }, [center, mapLoaded]);

  // Handle clusters (building markers)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add cluster markers
    clusters.forEach((cluster) => {
      const {
        center, reviewCount, avgRating, recommendationRatio, newestReview,
      } = cluster;

      // Color based on recommendation ratio
      let markerColor = '#95a5a6'; // Gray for neutral
      if (recommendationRatio >= 70) markerColor = '#27ae60'; // Green for good
      else if (recommendationRatio <= 30) markerColor = '#e74c3c'; // Red for poor

      const marker = new mapboxgl.Marker({
        color: markerColor,
      })
        .setLngLat([center.lng, center.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div class="cluster-popup">
                <h4>🏢 Building Reviews</h4>
                <div class="cluster-stats">
                  <div class="stat">${reviewCount} review${reviewCount > 1 ? 's' : ''}</div>
                  <div class="stat">${recommendationRatio}% recommend</div>
                  <div class="stat">Rating: ${avgRating}/10</div>
                </div>
                <div class="latest-review">
                  <strong>Latest:</strong> ${newestReview.recommend === 'yes' ? '✅' : '❌'}
                  ${newestReview.recommendNotes ? `<br><em>"${newestReview.recommendNotes}"</em>` : ''}
                </div>
                <button class="view-details-btn" data-cluster-id="${cluster.id}">View All Reviews</button>
              </div>
            `),
        )
        .addTo(map.current);

      // Add click handler for the cluster
      marker.getElement().addEventListener('click', () => {
        if (onClusterClick) {
          onClusterClick(cluster);
        }
      });

      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
  }, [clusters, mapLoaded, onClusterClick]);

  return (
    <div className="map-container">
      <div ref={mapContainer} className="map" />
      {!center && (
        <div className="map-loading">
          <p>Loading map...</p>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
